/**
 * The single error envelope every API response uses on a non-2xx.
 *
 * Shape: `{ error: { code, message, detail?, request_id } }`.
 *
 * `code` is a **stable string enum**. Clients branch on `code`; `message` is
 * human-facing prose and may be reworded, translated, or shortened at any time
 * without a contract change. Never branch on `message`, and never put a
 * customer identifier, an engagement ref, an org name or a service name into
 * one — a leaked error string discloses that an org is procuring a penetration
 * test, which is itself a fact about their security posture (PRD §7, S10).
 *
 * `request_id` is always present, including on 5xx: it is the one token a
 * support conversation can start from, and scope-9po returns the same value as
 * a response header so it survives a screenshot.
 */
import { z } from 'zod';
import { NonEmptyTextSchema, UuidSchema } from './primitives.js';
/**
 * Adding a code is a compatible change. Removing or repurposing one is not:
 * a client in the field is branching on it. `test/error.test.ts` pins this
 * list so a deletion has to be deliberate.
 */
export const ERROR_CODES = [
    /** Request body, query or path failed schema validation. 400. */
    'VALIDATION_FAILED',
    /** No session cookie, or the session is unknown. 401. */
    'UNAUTHENTICATED',
    /** Session was valid and has expired or been revoked. 401. */
    'SESSION_EXPIRED',
    /** Authenticated, but the staff role does not hold the capability. 403. */
    'FORBIDDEN',
    /**
     * The resource does not exist **or** is not this session's to see. 404 is
     * deliberate: a 403 confirms the id exists and is an enumeration oracle
     * (scope-5ht). Never distinguish the two cases, here or in the message.
     */
    'NOT_FOUND',
    /** Throttled — per email, per IP, per prefix, or the global breaker. 429. */
    'RATE_LIMITED',
    /** OTP code did not match. Identical shape and timing to an unknown email. */
    'OTP_INVALID',
    /** OTP challenge is past `expires_at`, or already consumed. */
    'OTP_EXPIRED',
    /** Too many failed verifies; the challenge is burnt and a resend is required. */
    'OTP_LOCKED',
    /**
     * The send may or may not have gone out and must **not** be retried
     * automatically; the user presses resend, which supersedes the prior
     * challenge (scope-9xn).
     */
    'OTP_RESEND_AVAILABLE',
    /** Same idempotency key replayed with a different payload. 409. */
    'IDEMPOTENCY_KEY_CONFLICT',
    /** Write lost a race, or the resource version moved under the caller. 409. */
    'CONFLICT',
    /** The requested transition is not an edge in DM-001 §5. 409. */
    'ILLEGAL_STATE_TRANSITION',
    /** The `OT_PT` promotion gate refused the request. 409. */
    'PROMOTION_REFUSED',
    /**
     * A customer submit was refused: a required, visible question has no
     * answer yet (DM-001 §5). `detail.question_ids` names them. 409.
     */
    'SUBMIT_INCOMPLETE',
    /**
     * An answer was written to a `mobilising` question while the engagement is
     * still pre-`approved`. DM-001 §2.1 puts those questions at tier 2 and the
     * tier-1 serve does not offer them, so the write plane refuses them too
     * (`docs/design/adaptive-scoping.md` §2). `detail.question_ids` names them.
     * 409.
     */
    'TIER_MISMATCH',
    /**
     * A submitted answer looks like a credential. S1 is an input-validation
     * control enforced server-side on every text and textarea write, and is not
     * subject to consultant override.
     */
    'SECRET_DETECTED',
    /**
     * The attachment is not `av_status = 'clean'`. S6 fails closed: an
     * attachment is not readable, parseable, or transmissible while it is
     * anything else.
     */
    'ATTACHMENT_NOT_CLEAN',
    /** Answers were submitted against a pack version that is no longer active. */
    'PACK_VERSION_MISMATCH',
    /** Body or upload exceeded the accepted size. 413. */
    'PAYLOAD_TOO_LARGE',
    /** Unhandled server fault. The detail is logged, never returned. 500. */
    'INTERNAL_ERROR',
    /** A dependency (database, mail, AV) is unavailable. 503. */
    'SERVICE_UNAVAILABLE',
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/**
 * Machine-readable context — field paths on a validation failure, `retry_after`
 * on a throttle. It is returned to the caller, so it carries no stack, no SQL,
 * no internal identifier and no third-party response body.
 */
export const ErrorDetailSchema = z.record(z.string(), z.unknown());
export const ErrorBodySchema = z.object({
    code: ErrorCodeSchema,
    message: NonEmptyTextSchema,
    detail: ErrorDetailSchema.optional(),
    request_id: UuidSchema,
});
export const ErrorEnvelopeSchema = z.object({ error: ErrorBodySchema });
//# sourceMappingURL=error.js.map